import { Injectable } from "@angular/core";
import { AddressModel } from "src/models/address.model";

import { HttpClientCustom } from "src/providers/custom/http-client";

@Injectable()
export class Genome {
  constructor(private httpClient: HttpClientCustom) {}

  async addRooms(addressId, data) {
    const url = `api/v1/customer/addresses/${addressId}/rooms`;
    return await this.httpClient.post(url, data);
  }

  async updateRoom(addressId, roomId, data) {
    const url = `api/v1/customer/addresses/${addressId}/rooms/${roomId}`;
    return await this.httpClient.put(url, data);
  }

  async deleteRoom(addressId, roomId) {
    const url = `api/v1/customer/addresses/${addressId}/rooms/${roomId}`;
    return await this.httpClient.delete(url);
  }

  async getRooms(addressId) {
    const url = `api/v1/instructions/addresses/${addressId}/address-rooms?includes=characteristics`;
    return await this.httpClient.get(url);
  }

  async getInventoryRoomObjects(params?: {page?: number; per_page?: number; consumable?: boolean | null; search?: string | null; address_id?: number | null}) {
    const stringifiedParams = this.stringifyParams({
      page: params?.page,
      per_page: params?.per_page
    });
    let filter = '';
    if (params?.consumable && params?.search) {
      filter += `&filters[consumable]=${params.consumable}&filters[name_search]=${params.search}`;
    } else if (params?.consumable !== undefined) {
      filter += `&filters[consumable]=${params.consumable}`;
    } else if (params?.search) {
      filter += `&filters[name_search]=${params.search}`;
    }
    if (params?.address_id) {
      filter += `&filters[address_id]=${params.address_id}`;
    }
    // with_restock_status: each consumable row carries `current_restock_order`
    // (its slice of the newest in-flight restock order) for item-level status.
    const url = `api/v1/customer/room-objects?includes=with_address_details,with_restock_status&${stringifiedParams}${filter}`;
    return this.httpClient.getFullRequest(url)
    .then(response => ({
        body: response.body,
        totalRecords: response.headers.get('X-Total-Records')
      }));
  }

  async getRoomsObjects(addressId) {
    const url = `api/v1/customer/room-objects?filters[address_id]=${addressId}&per_page=500`;
    const response = await this.httpClient.getFullRequest(url);
    const totalRecords = response.headers.get('X-Total-Records');

    if (!totalRecords || totalRecords < 501) {
      return response.body;
    }

    const allRecordsUrl = `api/v1/customer/room-objects?filters[address_id]=${addressId}&per_page=${totalRecords}`; 
    return await this.httpClient.get(allRecordsUrl);
  }

  stringifyParams(params) {
    const searchParams = new URLSearchParams();
    for (const key in params) {
      if (params[key] !== null && params[key] !== undefined) {
        searchParams.append(key, String(params[key]));
      }
    }
    return searchParams.toString();
  }

  async getRoomObject(roomObjectId) {
    const url = `api/v1/customer/room-objects/${roomObjectId}?includes=with_address_tasks,with_change_history,with_address_details,characteristics,with_address_task_lists,address_task_groups`;
    return await this.httpClient.get(url);
  }

  async addRoomObject(data) {
    const url = `api/v1/customer/room-objects/create-many`;
    return await this.httpClient.post(url, data);
  }

  async updateRoomObject(objectId, data) {
    const url = `api/v1/customer/room-objects/${objectId}`;
    return await this.httpClient.put(url, data);
  }

  async inactivateRoomObject(roomObjectId) {
    const url = `api/v1/customer/room-objects/${roomObjectId}/inactivate`;
    return await this.httpClient.post(url, {});
  }

  async getAllConsumableRoomObjects() {
    const url = `api/v1/customer/room-objects?filters[consumable]=true&includes=with_address_details&per_page=500`;
    const response = await this.httpClient.getFullRequest(url);
    const totalRecords = response.headers.get('X-Total-Records');
    if (!totalRecords || totalRecords < 501) {
      return response.body;
    }
    const allRecordsUrl = `api/v1/customer/room-objects?filters[consumable]=true&includes=with_address_details&per_page=${totalRecords}`;
    return await this.httpClient.get(allRecordsUrl);
  }

  async getCategoriesForRoom(category) {
    const url = `api/v1/customer/genome/categories?filters[room_category]=${category}`;
    const response = await this.httpClient.get(url);
    response.map((cat) => {
      cat['defaults'] = categoryDefaults[cat.name];
    });
    return response;
  }

  async getCategories() {
    const url = `api/v1/customer/genome/categories`;
    const response = await this.httpClient.get(url);
    response.map((category) => {
      category['defaults'] = categoryDefaults[category.name];
    });
    return response;
  }

  async getCharacteristics(categoryId) {
    const url = `api/v1/customer/genome/characteristic-types?category_id=${categoryId}`;
    return await this.httpClient.get(url);
  }

  async getObjectModel(categoryId, queryString) {
    const url = `api/v1/customer/genome/object-models?category_id=${categoryId}${queryString}`;
    return await this.httpClient.get(url);
  }

  async getAddressDetails(addressId) {
    const url = `api/v1/customer/address-details?address_id=${addressId}`;
    return await this.httpClient.get(url);
  }

  async saveAddressDetails(payload) {
    const url = `api/v1/customer/address-details`;
    return await this.httpClient.post(url, payload);
  }

  async getAddress(addressId, includes = 'plans'): Promise<AddressModel> {
    const includesParam = includes ? `includes=${includes}` : '';
    const url = `api/v1/customer/addresses/${addressId}?${includesParam}`;
    return await this.httpClient.get(url);
  }

  async addNewModel(data) {
    const url = `api/v1/customer/genome/object-models`;
    return await this.httpClient.post(url, data);
  }

  // Company-wide default "brand waterfall" for a category (backend source of truth).
  // Returns [] until the backend endpoint ships; callers fall back to the bundled defaults.
  async getPreferredBrandDefaults(categoryId) {
    const url = `api/v1/customer/genome/categories/${categoryId}/preferred-brands`;
    return await this.httpClient.get(url);
  }

  async getUpcomingMaintenances(addressId: number | string) {
    const url = `/api/v1/customer/room-object-maintenance-instances/to-do?address_id=${addressId}&upcoming_days=730&includes=steps,characteristics`;
    return await this.httpClient.get(url);
  }

  async getPastMaintenancesByRoomObject(
    addressId: number,
    roomObjectId: string
  ) {
    const url = `/api/v1/customer/room-object-maintenance-instances?address_id=${addressId}&filters[room_object_ids]=${roomObjectId}&includes=steps,characteristics`;
    return await this.httpClient.get(url);
  }

  async getUpcomingMaintenancesByRoomObject(addressId: number, roomObjectId) {
    const url = `/api/v1/customer/room-object-maintenance-instances/to-do?address_id=${addressId}&upcoming_days=730&filters[room_object_ids]=${roomObjectId}&includes=steps,characteristics`;
    return await this.httpClient.get(url);
  }

  async updateMaintenanceStatus(data) {
    const url = "/api/v1/customer/room-object-maintenance-instances";
    return await this.httpClient.post(url, data);
  }

  async createManyRoomObjects(data) {
    const url = "api/v1/customer/room-objects/create-many";
    return await this.httpClient.post(url, data);
  }

  static getDefaultInstallationYear(categoryName: string): number {
    const currentYear = new Date().getFullYear();
    const defaultAge = categoryDefaultAges[categoryName] || 10;
    return currentYear - defaultAge;
  }

  static getInstallationYear(installationDate: string): string {
    if (!installationDate) {
      return '';
    }
    const parts = installationDate.split('-');
    if (parts.length >= 1) {
      return parts[0];
    }
    return '';
  }

}

const categoryDefaultAges: { [key: string]: number } = {
  'Refrigerator': 13,
  'Dishwasher': 10,
  'Microwave': 9,
  'Washer': 11,
  'Dryer': 13,
  'Air Conditioning Unit': 15,
  'Heater': 15,
  'Furnace': 15,
  'Water Heater': 10,
  'Garbage Disposal': 10,
  'Range': 13,
  'Oven': 13,
  'Stove': 13,
  'Stove + Oven Combo': 13,
  'Grill': 10,
  'Pool Pump': 8,
  'Pool Heater': 8,
  'Hot Tub': 10,
  'TV': 7,
  'Furniture': 10,
  'Sofa': 10,
  'Bed': 10,
  'Mattress': 10,
  'Table': 15,
  'Chair': 12,
  'Desk': 15,
  'Washer + Dryer Combo': 11,
  'Freezer': 12,
  'Wine Cooler': 10,
  'Ice Maker': 8,
  'Trash Compactor': 10,
  'Hood Vent': 12,
  'Central Vacuum': 20,
  'Smoke Detector': 10,
  'Carbon Monoxide Detector': 7,
  'Fire Extinguisher': 10,
  'Thermostat': 10,
  'Ceiling Fan': 15,
  'Garage Door Opener': 12,
  'Sump Pump': 10,
  'Water Softener': 12,
  'Septic System': 25,
  'Solar Panel': 10,
  'Generator': 15,
  'Dehumidifier': 8,
  'Humidifier': 8,
  'Air Purifier': 5,
  'Vacuum': 8,
  'Lighting': 10,
  'Tree': 20,
};

const categoryDefaults = {
  'Air Filter': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Paper Towels': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '5',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Oil': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Tissue': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '4',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Sugar': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Aluminum Foil': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '10',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Tea': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '8',
    opportunisticOrderLevel: '12',
    maxQuantity: '16',
    currentQuantity: '16'
  },
  'Coffee Pod': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '12',
    opportunisticOrderLevel: '18',
    maxQuantity: '24',
    currentQuantity: '24'
  },
  'Coffee Creamer': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Dishwasher Detergent': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '18',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Dish Towel': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'All Purpose Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Batteries': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '8',
    opportunisticOrderLevel: '12',
    maxQuantity: '16',
    currentQuantity: '16'
  },
  'Seasoning': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '7',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Lighter': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '4',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Dryer Sheets': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '20',
    opportunisticOrderLevel: '30',
    maxQuantity: '40',
    currentQuantity: '40'
  },
  'Mat': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Hair Dryer': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Towels': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Bath Soap': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '5',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Hand Soap': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Sheets': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '50',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Fire Extinguisher': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '30',
    maxRestockingPrice: '50',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Ironing Board': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '35',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Iron': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '45',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Spatula': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Coffee Pot': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '45',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Furniture Polish': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '10',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Stain Remover': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Oven Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '10',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Bleach': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Light Bulb': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Beach Chair': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Umbrella': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Grill Brush': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Blender': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '30',
    maxRestockingPrice: '60',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Surf Board': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '200',
    maxRestockingPrice: '400',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Corkscrew / Wine Opener': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Cotton Ball': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '4',
    restockingNotes: '',
    minQuantity: '40',
    opportunisticOrderLevel: '60',
    maxQuantity: '80',
    currentQuantity: '80'
  },
  'Food': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Dishwasher Pods': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '12',
    maxRestockingPrice: '20',
    restockingNotes: '',
    minQuantity: '10',
    opportunisticOrderLevel: '20',
    maxQuantity: '30',
    currentQuantity: '30'
  },
  'Body Wash': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Trash Bag': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '15',
    opportunisticOrderLevel: '25',
    maxQuantity: '35',
    currentQuantity: '35'
  },
  'Shower Gel / Soap': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Hand Sanitizer': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Lotion': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Fabric Softener': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '6',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Stirrer / Stick': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '20',
    opportunisticOrderLevel: '40',
    maxQuantity: '60',
    currentQuantity: '60'
  },
  'Cooking Spray': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Ziplock Bag': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '10',
    opportunisticOrderLevel: '20',
    maxQuantity: '30',
    currentQuantity: '30'
  },
  'Plastic Wrap': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Napkin': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '40',
    opportunisticOrderLevel: '60',
    maxQuantity: '80',
    currentQuantity: '80'
  },
  'Sidewalk Salt': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Basket': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '20',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Paper': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '40',
    opportunisticOrderLevel: '60',
    maxQuantity: '80',
    currentQuantity: '80'
  },
  'Disinfectant Wipe': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '20',
    opportunisticOrderLevel: '30',
    maxQuantity: '40',
    currentQuantity: '40'
  },
  'Propane Tank': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '45',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Firewood': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '8',
    opportunisticOrderLevel: '12',
    maxQuantity: '16',
    currentQuantity: '16'
  },
  'Glass Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Bathroom Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Toaster': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '45',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Electric Kettle': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '45',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Floor Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '10',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Carpet Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '10',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Balsamic Vinegar': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '6',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Toilet Cleaner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Cart': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '30',
    maxRestockingPrice: '60',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Toys': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Grill Tools': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Wine Glass': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Boogie Board': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'TV': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '200',
    maxRestockingPrice: '400',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Q Tip': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '40',
    opportunisticOrderLevel: '60',
    maxQuantity: '80',
    currentQuantity: '80'
  },
  'Toothpaste': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Toothbrush': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '5',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Wagon': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '40',
    maxRestockingPrice: '80',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Baby Chair': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '30',
    maxRestockingPrice: '60',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Other': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '20',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Cooler': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '50',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Bicycle': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '100',
    maxRestockingPrice: '200',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Pitcher': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '20',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Oven Mitt / Potholder': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Rack': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Toilet Paper': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '8',
    opportunisticOrderLevel: '12',
    maxQuantity: '16',
    currentQuantity: '16'
  },
  'Conditioner': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Plate': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Bowl': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Knife': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Fork': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Shampoo': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Sponge': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '4',
    restockingNotes: '',
    minQuantity: '3',
    opportunisticOrderLevel: '4',
    maxQuantity: '5',
    currentQuantity: '5'
  },
  'Detergent': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Dish Soap': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Salt': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '4',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Soap': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '5',
    restockingNotes: '',
    minQuantity: '3',
    opportunisticOrderLevel: '4',
    maxQuantity: '5',
    currentQuantity: '5'
  },
  'Hangers': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '12',
    opportunisticOrderLevel: '18',
    maxQuantity: '24',
    currentQuantity: '24'
  },
  'Crib': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '100',
    maxRestockingPrice: '200',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Cofee Maker': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '50',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Spoon': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Outdoor Game': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Coffee': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '15',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Coffee Filter': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '20',
    opportunisticOrderLevel: '30',
    maxQuantity: '40',
    currentQuantity: '40'
  },
  'Pepper': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Pillow Case': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Pillow': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Blanket': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '35',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Vacuum': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '100',
    maxRestockingPrice: '200',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Glass / Cup': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '6',
    opportunisticOrderLevel: '9',
    maxQuantity: '12',
    currentQuantity: '12'
  },
  'Mug': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Cushion': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Table': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '50',
    maxRestockingPrice: '100',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Dustpan': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Mop': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Broom': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '8',
    maxRestockingPrice: '20',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'TV Remote Control': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Can Opener': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Tongs': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Pizza Cutter': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Wipes': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'First Aid Kit': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Pan / Skillet': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '20',
    maxRestockingPrice: '40',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Pot': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '50',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Sweetener': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '6',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Wine': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '12',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Candy': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Comb': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '2',
    maxRestockingPrice: '5',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Board Game': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '15',
    maxRestockingPrice: '30',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  },
  'Bucket': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Scoop': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '3',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Cookie Sheet': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '20',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Kitchen Utensil': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '5',
    maxRestockingPrice: '12',
    restockingNotes: '',
    minQuantity: '4',
    opportunisticOrderLevel: '6',
    maxQuantity: '8',
    currentQuantity: '8'
  },
  'Cutting Board': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '10',
    maxRestockingPrice: '25',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Kitchen Applicance': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '25',
    maxRestockingPrice: '50',
    restockingNotes: '',
    minQuantity: '1',
    opportunisticOrderLevel: '2',
    maxQuantity: '3',
    currentQuantity: '3'
  },
  'Air Freshener': {
    deliveryTimingPreferences: 'Between guest stays',
    whereToOrder: '',
    minRestockingPrice: '4',
    maxRestockingPrice: '8',
    restockingNotes: '',
    minQuantity: '2',
    opportunisticOrderLevel: '3',
    maxQuantity: '4',
    currentQuantity: '4'
  }
}
